import type { ReactNode } from 'react'
import { Navigate, useLocation, useNavigate, useOutletContext, useParams } from 'react-router'

import { QuantConfigPane } from '@/app/components/QuantConfigPane'
import { QuantLiveDetail } from '@/app/components/QuantLiveDetail'
import { QuantLivePane } from '@/app/components/QuantLivePane'
import { QuantRunDetail } from '@/app/components/QuantRunDetail'
import { QuantRunsPane } from '@/app/components/QuantRunsPane'
import type { QuantShellContext } from '@/app/pages/QuantJupyter'
import { useQuantLiveSessions, type QuantLiveListState } from '@/app/hooks/useQuantLiveSessions'
import { useQuantRuns } from '@/app/hooks/useQuantRuns'
import { useQuantStrategies } from '@/app/hooks/useQuantStrategies'
import { isActive } from '@/app/lib/quant/liveView'
import type { Run, Strategy } from '@/app/lib/quant/types'
import { heldByOther } from '@/app/lib/quant/slotState'
import { Button } from '@/ui/Button'
import { QuantUnavailable } from '@/ui/QuantUnavailable'
import { formatReturn, returnTone } from '@/ui/lib/quantFormat'
import { QuantStrategyHeader, type QuantStrategyStat } from '@/ui/QuantStrategyHeader'
import { QuantStrategyList, type QuantStrategyListItem } from '@/ui/QuantStrategyList'

type Pane = 'config' | 'runs' | 'live'

function completedReturns(runs: Run[]): number[] {
  return runs
    .filter((r) => r.status === 'completed' && r.return_pct !== null)
    .map((r) => r.return_pct as number)
}

function Message({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8 font-mono text-[12px] text-paper-50">{children}</div>
  )
}

/**
 * The Strategies screen (spec 036 D-3, handoff 3): the strategy list with the
 * run-slots meter on the left; a strategy header plus Config / Runs / Live on
 * the right. One component serves every `/strategies/*` route; the pane
 * is read from the path. Data comes only through the Quant hooks.
 */
export default function QuantStrategies() {
  const { slug, runId, sessionId } = useParams<{ slug?: string; runId?: string; sessionId?: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const strategiesState = useQuantStrategies()
  const runsState = useQuantRuns()
  const liveState = useQuantLiveSessions()

  if (strategiesState.status === 'loading') return null
  if (strategiesState.status === 'error' && slug !== undefined && (runId !== undefined || sessionId !== undefined)) {
    // Strategies are down but the run and live endpoints may not be: keep the run or session reachable (AC 25; spec 041 AC 24).
    const stub: Strategy = {
      slug,
      name: slug,
      description: '',
      params_schema: null,
      error: null,
      run_count: 0,
      last_return_pct: null,
      preview: null,
      preview_error: null,
      live: null,
      live_error: null,
    }
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="px-7 pt-4">
          <QuantUnavailable message={strategiesState.message} onRetry={strategiesState.retry} />
        </div>
        <div className="relative min-h-0 flex-1">
          {sessionId !== undefined ? (
            <QuantLiveDetail key={sessionId} strategy={stub} sessionId={sessionId} />
          ) : (
            runId !== undefined && <QuantRunDetail strategy={stub} runId={runId} />
          )}
        </div>
      </div>
    )
  }
  if (strategiesState.status === 'error') {
    return (
      <div className="flex flex-1 items-start justify-center p-8">
        <div className="w-full max-w-[640px]">
          <QuantUnavailable message={strategiesState.message} onRetry={strategiesState.retry} />
        </div>
      </div>
    )
  }

  const strategies = strategiesState.strategies
  if (strategies.length === 0) return <Message>No strategies on disk.</Message>
  if (slug === undefined) {
    return <Navigate to={`/strategies/${strategies[0].slug}/config`} replace />
  }

  const segment = location.pathname.split('/')[3]
  const pane: Pane = segment === 'runs' ? 'runs' : segment === 'live' ? 'live' : 'config'
  const runs = runsState.status === 'loaded' ? runsState.runs : []
  const active = runsState.status === 'loaded' ? runsState.active : undefined
  const max = runsState.status === 'loaded' ? runsState.max : undefined
  const ownActive = runsState.status === 'loaded' ? runsState.ownActive : undefined
  const held = heldByOther(active, max, ownActive)
  const selected = strategies.find((s) => s.slug === slug)
  const activeByStrategy = new Map<string, number>()
  if (liveState.status === 'loaded') {
    for (const session of liveState.sessions) {
      if (isActive(session)) activeByStrategy.set(session.strategy_slug, (activeByStrategy.get(session.strategy_slug) ?? 0) + 1)
    }
  }

  const items: QuantStrategyListItem[] = strategies.map((s) => {
    const mine = runs.filter((r) => r.strategy_slug === s.slug)
    const running = mine.filter((r) => r.status === 'running').length
    const ret = s.error ? null : s.last_return_pct
    return {
      slug: s.slug,
      name: s.error ? s.slug : (s.name ?? s.slug),
      subtitle: s.error ? 'strategy.toml' : s.slug,
      flag: `${s.run_count} runs${running > 0 ? ` · ${running} running` : ''}`,
      configError: s.error !== null,
      returnText: s.error ? '' : formatReturn(ret),
      returnTone: returnTone(ret),
      live: (activeByStrategy.get(s.slug) ?? 0) > 0,
      selected: s.slug === slug,
      href: `/strategies/${s.slug}/config`,
    }
  })

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[minmax(210px,260px)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
      <QuantStrategyList
        items={items}
        active={active}
        max={max}
        heldByOther={held}
        onNavigate={(href) => navigate(href)}
        notice={
          runsState.status === 'error' ? (
            <div role="alert" className="flex items-center gap-2 font-mono text-[11px] text-alert">
              Runs unavailable.
              <Button variant="secondary" size="sm" onClick={runsState.retry}>
                Retry
              </Button>
            </div>
          ) : undefined
        }
      />
      <section className="flex min-h-0 min-w-0 flex-col">
        {selected === undefined ? (
          <Message>Unknown strategy</Message>
        ) : (
          <StrategyRight
            strategy={selected}
            pane={pane}
            runId={runId}
            sessionId={sessionId}
            runs={runs.filter((r) => r.strategy_slug === selected.slug)}
            liveCount={activeByStrategy.get(selected.slug) ?? 0}
            liveState={liveState}
            active={active}
            max={max}
            heldByOther={held}
            onNavigate={(href) => navigate(href)}
          />
        )}
      </section>
    </div>
  )
}

interface StrategyRightProps {
  strategy: Strategy
  pane: Pane
  runId: string | undefined
  sessionId: string | undefined
  runs: Run[]
  liveCount: number
  liveState: QuantLiveListState & { retry: () => void }
  active: number | undefined
  max: number | undefined
  heldByOther: boolean
  onNavigate: (href: string) => void
}

function StrategyRight({ strategy, pane, runId, sessionId, runs, liveCount, liveState, active, max, heldByOther, onNavigate }: StrategyRightProps) {
  const base = `/strategies/${strategy.slug}`
  const returns = completedReturns(runs)
  const best = returns.length > 0 ? Math.max(...returns) : null
  const last = runs.find((r) => r.status === 'completed' && r.return_pct !== null)?.return_pct ?? null

  const shell = useOutletContext<QuantShellContext | undefined>()

  const stats: QuantStrategyStat[] | undefined = strategy.error
    ? undefined
    : [
        { label: 'Runs', value: String(strategy.run_count) },
        { label: 'Best', value: formatReturn(best), tone: returnTone(best) },
        { label: 'Last', value: formatReturn(last), tone: returnTone(last) },
        liveCount > 0
          ? { label: 'Live', value: `${liveCount} paper`, accent: true }
          : { label: 'Live', value: '—', tone: 'none' },
      ]

  return (
    <>
      <QuantStrategyHeader
        name={strategy.error ? strategy.slug : (strategy.name ?? strategy.slug)}
        slug={strategy.error ? 'strategy.toml' : strategy.slug}
        stats={stats}
        description={strategy.description ?? undefined}
        compact={(pane === 'runs' && runId !== undefined) || (pane === 'live' && sessionId !== undefined)}
        tabs={[
          { label: 'Config', href: `${base}/config`, active: pane === 'config' },
          { label: 'Runs', href: `${base}/runs`, active: pane === 'runs', count: String(strategy.run_count) },
          { label: 'Live', href: `${base}/live`, active: pane === 'live', count: liveCount > 0 ? String(liveCount) : undefined },
        ]}
        onNavigate={onNavigate}
      />
      <div className="relative min-h-0 flex-1">
        {pane === 'config' && <QuantConfigPane key={strategy.slug} strategy={strategy} active={active} max={max} heldByOther={heldByOther} feed={shell?.feed} live={liveState.status === 'loaded' ? { active: liveState.active, max: liveState.max, ownActive: liveState.ownActive } : null} />}
        {pane === 'runs' && <QuantRunsPane strategy={strategy} runId={runId} />}
        {pane === 'live' && sessionId === undefined && <QuantLivePane key={strategy.slug} strategy={strategy} state={liveState} />}
        {pane === 'live' && sessionId !== undefined && <QuantLiveDetail key={sessionId} strategy={strategy} sessionId={sessionId} />}
      </div>
    </>
  )
}
